import React, { useEffect, useState, useRef, useCallback } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, FileText, Loader2, AlertCircle, Check, X, Activity } from 'lucide-react'
import { AlignedWaveformPlayer } from '../components/AlignedWaveformPlayer'
import { computeBidirectionalDTW, getAlignmentSegments, DTWResult, AlignmentSegment } from '../utils/audioAnalysis'

interface LogData {
  timestamp?: string
  status?: string
  details?: any
  [key: string]: any
}

interface AudioFile {
  filename: string
  url: string
  duration?: number
  size?: number
}

interface MixPairInfo {
  index: number
  track_prev: {
    hashName: string
  }
  track_next: {
    hashName: string
  }
}

interface DiffLine {
  type: 'equal' | 'delete' | 'insert'
  backendLine?: string
  userLine?: string
  backendLineNum?: number
  userLineNum?: number
}

export const LogComparisonPage: React.FC = () => {
  const { instance, queue, pairIndex } = useParams<{ 
    instance: string
    queue: string 
    pairIndex: string
  }>()
  const navigate = useNavigate()
  
  const [backendLog, setBackendLog] = useState<LogData | null>(null)
  const [userLog, setUserLog] = useState<LogData | null>(null)
  
  // Prevtrack audio files
  const [prevtrackExperimentAudio, setPrevtrackExperimentAudio] = useState<AudioFile | null>(null)
  const [prevtrackUserAudio, setPrevtrackUserAudio] = useState<AudioFile | null>(null)
  const [selectedPrevtrackUserAudio, setSelectedPrevtrackUserAudio] = useState<File | null>(null)
  
  // Nexttrack audio files
  const [nexttrackExperimentAudio, setNexttrackExperimentAudio] = useState<AudioFile | null>(null)
  const [nexttrackUserAudio, setNexttrackUserAudio] = useState<AudioFile | null>(null)
  const [selectedNexttrackUserAudio, setSelectedNexttrackUserAudio] = useState<File | null>(null)
  
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedUserLog, setSelectedUserLog] = useState<File | null>(null)
  const [diffLines, setDiffLines] = useState<DiffLine[]>([])
  
  // DTW results for both groups
  const [prevtrackDtwResult, setPrevtrackDtwResult] = useState<DTWResult | null>(null)
  const [prevtrackAlignmentSegments, setPrevtrackAlignmentSegments] = useState<AlignmentSegment[]>([])
  const [isPrevtrackCalculatingDTW, setIsPrevtrackCalculatingDTW] = useState(false)
  
  const [nexttrackDtwResult, setNexttrackDtwResult] = useState<DTWResult | null>(null)
  const [nexttrackAlignmentSegments, setNexttrackAlignmentSegments] = useState<AlignmentSegment[]>([])
  const [isNexttrackCalculatingDTW, setIsNexttrackCalculatingDTW] = useState(false)
  
  const backendLogRef = useRef<HTMLDivElement>(null)
  const userLogRef = useRef<HTMLDivElement>(null)
  
  // Audio buffer refs for both groups
  const prevtrackExperimentBufferRef = useRef<AudioBuffer | null>(null)
  const prevtrackUserBufferRef = useRef<AudioBuffer | null>(null)
  const nexttrackExperimentBufferRef = useRef<AudioBuffer | null>(null)
  const nexttrackUserBufferRef = useRef<AudioBuffer | null>(null)

  useEffect(() => {
    if (!instance || !queue || !pairIndex) {
      navigate('/')
      return
    }

    const fetchBackendData = async () => {
      try {
        // URL 中的 pairIndex 需要转换为文件系统中的索引（从 0 开始）
        const urlPairIndex = parseInt(pairIndex)
        const fileSystemIndex = urlPairIndex - 1
        
        console.log('URL pair index:', urlPairIndex)
        console.log('File system index:', fileSystemIndex)

        // 获取实验日志 res/实验名/播放队列/logs/${fileSystemIndex}.log
        const logResponse = await fetch(`/api/experiments/${instance}/${queue}/logs/${fileSystemIndex}.log`)
        if (logResponse.ok) {
          const logData = await logResponse.json()
          setBackendLog(logData)
          console.log('✓ Log file loaded:', `${fileSystemIndex}.log`)
        } else {
          console.warn('✗ Backend log not found:', `${fileSystemIndex}.log`)
        }

        // 获取 mixinfo.json 以获取 hash 值
        try {
          const mixinfoResponse = await fetch(`/api/experiments/${instance}/${queue}/mixinfo`)
          if (mixinfoResponse.ok) {
            const mixinfo = await mixinfoResponse.json()
            console.log('✓ mixinfo.json loaded successfully')
            
            // mixinfo.json 中的 index 也是从 0 开始的
            const currentPair = mixinfo.mix_pairs?.find((p: MixPairInfo) => p.index === fileSystemIndex)
            const nextPair = mixinfo.mix_pairs?.find((p: MixPairInfo) => p.index === fileSystemIndex + 1)
            
            console.log('Current pair (index ' + fileSystemIndex + ') found:', !!currentPair)
            console.log('Next pair (index ' + (fileSystemIndex + 1) + ') found:', !!nextPair)

            if (currentPair) {
              const currentHash = currentPair.track_prev.hashName
              console.log('Current hash:', currentHash)

              // 加载 prevtrack 音频 (当前 pair 的 syncout)
              // 文件名格式: ${fileSystemIndex}_${hash}_merged_syncout.mp3
              const prevtrackUrl = `/audio-api/${instance}/${queue}/transitions_merged/${fileSystemIndex}_${currentHash}_merged_syncout.mp3`
              console.log('Attempting to load prevtrack audio:', prevtrackUrl)
              
              try {
                const prevtrackResponse = await fetch(prevtrackUrl, { method: 'HEAD' })
                console.log('Prevtrack response status:', prevtrackResponse.status)
                
                if (prevtrackResponse.ok) {
                  setPrevtrackExperimentAudio({
                    filename: `${fileSystemIndex}_${currentHash}_merged_syncout.mp3`,
                    url: prevtrackUrl,
                  })
                  console.log('✓ Prevtrack experiment audio loaded:', prevtrackUrl)
                } else {
                  console.warn('✗ Prevtrack experiment audio not found (status ' + prevtrackResponse.status + '):', prevtrackUrl)
                }
              } catch (err) {
                console.error('✗ Error loading prevtrack experiment audio:', err)
              }
            } else {
              console.warn('Current pair not found in mixinfo.json for index:', fileSystemIndex)
            }

            if (nextPair) {
              const nextHash = nextPair.track_prev.hashName
              console.log('Next hash:', nextHash)

              // 加载 nexttrack 音频 (下一个 pair 的 syncin)
              // 文件名格式: ${fileSystemIndex+1}_${nextHash}_merged_syncin.mp3
              const nexttrackUrl = `/audio-api/${instance}/${queue}/transitions_merged/${fileSystemIndex + 1}_${nextHash}_merged_syncin.mp3`
              console.log('Attempting to load nexttrack audio:', nexttrackUrl)
              
              try {
                const nexttrackResponse = await fetch(nexttrackUrl, { method: 'HEAD' })
                console.log('Nexttrack response status:', nexttrackResponse.status)
                
                if (nexttrackResponse.ok) {
                  setNexttrackExperimentAudio({
                    filename: `${fileSystemIndex + 1}_${nextHash}_merged_syncin.mp3`,
                    url: nexttrackUrl,
                  })
                  console.log('✓ Nexttrack experiment audio loaded:', nexttrackUrl)
                } else {
                  console.warn('✗ Nexttrack experiment audio not found (status ' + nexttrackResponse.status + '):', nexttrackUrl)
                }
              } catch (err) {
                console.error('✗ Error loading nexttrack experiment audio:', err)
              }
            } else {
              console.warn('Next pair not found in mixinfo.json for index:', fileSystemIndex + 1)
            }
          } else {
            console.error('mixinfo.json not found (status ' + mixinfoResponse.status + ')')
            setError('无法加载 mixinfo.json，状态码: ' + mixinfoResponse.status)
          }
        } catch (err) {
          console.error('Error fetching mixinfo.json:', err)
          setError('获取 mixinfo.json 失败: ' + (err instanceof Error ? err.message : String(err)))
        }

        setLoading(false)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load backend data')
        setLoading(false)
      }
    }

    fetchBackendData()
  }, [instance, queue, pairIndex, navigate])

  const handleUserLogUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file && file.name.endsWith('.log')) {
      setSelectedUserLog(file)
      const reader = new FileReader()
      reader.onload = (e) => {
        const result = e.target?.result as string
        const logLines = result.split('\n').filter((line: string) => line.trim())
        setUserLog({
          content: result,
          lines: logLines,
          lineCount: logLines.length,
          filename: file.name
        })
      }
      reader.readAsText(file)
    } else {
      alert('请选择 LOG 文件')
    }
  }

  const handlePrevtrackUserAudioUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file && file.type.startsWith('audio/')) {
      setSelectedPrevtrackUserAudio(file)
      const url = URL.createObjectURL(file)
      setPrevtrackUserAudio({
        filename: file.name,
        url: url,
        size: file.size
      })
    } else {
      alert('请选择音频文件')
    }
  }

  const handleNexttrackUserAudioUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file && file.type.startsWith('audio/')) {
      setSelectedNexttrackUserAudio(file)
      const url = URL.createObjectURL(file)
      setNexttrackUserAudio({
        filename: file.name,
        url: url,
        size: file.size
      })
    } else {
      alert('请选择音频文件')
    }
  }

  const formatLogData = (data: any) => {
    if (!data) return 'No data available'
    
    // 显示LOG文件的原始内容
    if (data.content) {
      return data.content
    }
    
    return 'No log content available'
  }

  // 简单的diff算法
  const calculateDiff = useCallback((backendLines: string[], userLines: string[]) => {
    const diff: DiffLine[] = []
    let backendIndex = 0
    let userIndex = 0
    
    while (backendIndex < backendLines.length || userIndex < userLines.length) {
      const backendLine = backendLines[backendIndex]
      const userLine = userLines[userIndex]
      
      if (backendIndex >= backendLines.length) {
        // 测试日志有额外的行
        diff.push({
          type: 'insert',
          userLine,
          userLineNum: userIndex + 1
        })
        userIndex++
      } else if (userIndex >= userLines.length) {
        // 实验日志有额外的行
        diff.push({
          type: 'delete',
          backendLine,
          backendLineNum: backendIndex + 1
        })
        backendIndex++
      } else if (backendLine === userLine) {
        // 行相同
        diff.push({
          type: 'equal',
          backendLine,
          userLine,
          backendLineNum: backendIndex + 1,
          userLineNum: userIndex + 1
        })
        backendIndex++
        userIndex++
      } else {
        // 行不同，标记为删除+插入
        diff.push({
          type: 'delete',
          backendLine,
          backendLineNum: backendIndex + 1
        })
        diff.push({
          type: 'insert',
          userLine,
          userLineNum: userIndex + 1
        })
        backendIndex++
        userIndex++
      }
    }
    
    return diff
  }, [])

    // 同步滚动处理（垂直和水平）
  const handleScroll = useCallback((source: 'experiment' | 'test') => {
    return (event: React.UIEvent<HTMLDivElement>) => {
      const sourceElement = event.currentTarget
      const targetElement = source === 'experiment' ? userLogRef.current : backendLogRef.current
      
      if (targetElement && sourceElement) {
        // 同步垂直滚动
        const maxVerticalScroll = sourceElement.scrollHeight - sourceElement.clientHeight
        if (maxVerticalScroll > 0) {
          const verticalScrollRatio = sourceElement.scrollTop / maxVerticalScroll
          const targetMaxVerticalScroll = targetElement.scrollHeight - targetElement.clientHeight
          const targetScrollTop = verticalScrollRatio * targetMaxVerticalScroll
          targetElement.scrollTop = targetScrollTop
        }
        
        // 同步水平滚动
        const maxHorizontalScroll = sourceElement.scrollWidth - sourceElement.clientWidth
        if (maxHorizontalScroll > 0) {
          const horizontalScrollRatio = sourceElement.scrollLeft / maxHorizontalScroll
          const targetMaxHorizontalScroll = targetElement.scrollWidth - targetElement.clientWidth
          const targetScrollLeft = horizontalScrollRatio * targetMaxHorizontalScroll
          targetElement.scrollLeft = targetScrollLeft
        }
      }
    }
  }, [])

  // 当日志数据更新时计算diff
  useEffect(() => {
    if (backendLog && userLog && backendLog.lines && userLog.lines) {
      const diff = calculateDiff(backendLog.lines, userLog.lines)
      setDiffLines(diff)
    } else {
      setDiffLines([])
    }
  }, [backendLog, userLog, calculateDiff])

  const compareFields = (backendData: any, userData: any) => {
    const differences: string[] = []
    
    if (!backendData && !userData) return differences
    if (!backendData) return ['Experiment log not available']
    if (!userData) return ['Test log not uploaded']

    // 现在前后端都是LOG文件格式，进行文本对比
    if (backendData.content && userData.content) {
      // 内容完全相同检查
      if (backendData.content === userData.content) {
        differences.push('✅ Log contents are identical')
        return differences
      }
      
      // 行数对比
      if (backendData.lineCount === userData.lineCount) {
        differences.push(`✅ Line count match: ${backendData.lineCount} lines`)
      } else {
        differences.push(`❌ Different line counts: Experiment(${backendData.lineCount}) vs Test(${userData.lineCount})`)
      }
      
      // 简单的行级对比（显示前几个不同的行）
      const backendLines = backendData.lines || []
      const userLines = userData.lines || []
      const maxLines = Math.min(backendLines.length, userLines.length, 10) // 只比较前10行
      
      for (let i = 0; i < maxLines; i++) {
        if (backendLines[i] !== userLines[i]) {
          differences.push(`❌ Line ${i + 1} differs`)
          break // 只显示第一个不同的行
        }
      }
      
      return differences
    }

    differences.push('❌ Cannot compare: Invalid log format')
    return differences
  }

  // 计算 Prevtrack DTW 相似度
  const handleCalculatePrevtrackDTW = useCallback(async () => {
    if (!prevtrackExperimentBufferRef.current || !prevtrackUserBufferRef.current) {
      setError('请确保 Prevtrack 的两个音频都已加载')
      return
    }

    setIsPrevtrackCalculatingDTW(true)
    setError(null)

    try {
      console.log('开始计算 Prevtrack DTW...')
      
      await new Promise(resolve => setTimeout(resolve, 100))
      
      const result = computeBidirectionalDTW(
        prevtrackExperimentBufferRef.current,
        prevtrackUserBufferRef.current,
        2048 // hopSize
      )
      
      setPrevtrackDtwResult(result)
      
      const segments = getAlignmentSegments(
        result.path,
        prevtrackExperimentBufferRef.current.duration,
        prevtrackUserBufferRef.current.duration,
        prevtrackExperimentBufferRef.current.sampleRate,
        2048
      )
      
      setPrevtrackAlignmentSegments(segments)
      
      console.log('Prevtrack DTW计算完成:', result)
    } catch (err) {
      console.error('Prevtrack DTW计算错误:', err)
      setError('Prevtrack DTW计算失败: ' + (err instanceof Error ? err.message : String(err)))
    } finally {
      setIsPrevtrackCalculatingDTW(false)
    }
  }, [])

  // 计算 Nexttrack DTW 相似度
  const handleCalculateNexttrackDTW = useCallback(async () => {
    if (!nexttrackExperimentBufferRef.current || !nexttrackUserBufferRef.current) {
      setError('请确保 Nexttrack 的两个音频都已加载')
      return
    }

    setIsNexttrackCalculatingDTW(true)
    setError(null)

    try {
      console.log('开始计算 Nexttrack DTW...')
      
      await new Promise(resolve => setTimeout(resolve, 100))
      
      const result = computeBidirectionalDTW(
        nexttrackExperimentBufferRef.current,
        nexttrackUserBufferRef.current,
        2048 // hopSize
      )
      
      setNexttrackDtwResult(result)
      
      const segments = getAlignmentSegments(
        result.path,
        nexttrackExperimentBufferRef.current.duration,
        nexttrackUserBufferRef.current.duration,
        nexttrackExperimentBufferRef.current.sampleRate,
        2048
      )
      
      setNexttrackAlignmentSegments(segments)
      
      console.log('Nexttrack DTW计算完成:', result)
    } catch (err) {
      console.error('Nexttrack DTW计算错误:', err)
      setError('Nexttrack DTW计算失败: ' + (err instanceof Error ? err.message : String(err)))
    } finally {
      setIsNexttrackCalculatingDTW(false)
    }
  }, [])



  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">加载对比数据...</p>
        </div>
      </div>
    )
  }

  const differences = compareFields(backendLog, userLog)

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link
                to={`/experiments/${instance}/${queue}/${pairIndex}`}
                className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                返回 Mix Pair
              </Link>
              <span className="text-gray-400 dark:text-gray-500">/</span>
              <span className="text-gray-900 dark:text-white font-medium">
                Mix Pair {pairIndex} 日志对比
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mr-2" />
              <span className="text-red-600 dark:text-red-400">{error}</span>
            </div>
          </div>
        )}

        {/* Comparison Results */}
        <div className="space-y-8">
          {/* Log Comparison */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
              日志对比结果
            </h3>

            {/* Upload Test Log */}
            <div className="mb-6 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
              <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-3">
                上传测试日志
              </h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    选择 LOG 日志文件
                  </label>
                  <input
                    type="file"
                    accept=".log"
                    onChange={handleUserLogUpload}
                    className="block w-full text-sm text-gray-500 dark:text-gray-400
                      file:mr-4 file:py-2 file:px-4
                      file:rounded-full file:border-0
                      file:text-sm file:font-semibold
                      file:bg-primary-50 file:text-primary-700
                      hover:file:bg-primary-100"
                  />
                </div>
                {selectedUserLog && (
                  <div className="flex items-center text-sm text-green-600 dark:text-green-400">
                    <Check className="h-4 w-4 mr-2" />
                    已上传: {selectedUserLog.name}
                  </div>
                )}
              </div>
            </div>
            
            {/* Differences Summary */}
            <div className="mb-6">
              <div className="flex items-center space-x-2 mb-3">
                <FileText className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                <span className="font-medium text-gray-900 dark:text-white">差异摘要</span>
              </div>
              {differences.length === 0 ? (
                <div className="flex items-center text-green-600 dark:text-green-400">
                  <Check className="h-4 w-4 mr-2" />
                  日志文件完全匹配
                </div>
              ) : (
                <div className="space-y-2">
                  {differences.map((diff, index) => {
                    const isSuccess = diff.includes('✅')
                    const isError = diff.includes('❌')
                    const colorClass = isSuccess 
                      ? 'text-green-600 dark:text-green-400' 
                      : isError 
                        ? 'text-red-600 dark:text-red-400'
                        : 'text-orange-600 dark:text-orange-400'
                    
                    return (
                      <div key={index} className={`flex items-center ${colorClass}`}>
                        {isSuccess ? (
                          <Check className="h-4 w-4 mr-2" />
                        ) : (
                          <X className="h-4 w-4 mr-2" />
                        )}
                        {diff}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Detailed Comparison */}
            {diffLines.length > 0 ? (
              <div>
                <div className="grid grid-cols-2 gap-4 mb-2">
                  <h4 className="font-medium text-gray-900 dark:text-white">实验日志</h4>
                  <h4 className="font-medium text-gray-900 dark:text-white">测试日志</h4>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {/* Experiment diff view */}
                  <div 
                    className="bg-gray-50 dark:bg-gray-700 rounded-lg max-h-96 overflow-auto border"
                    ref={backendLogRef}
                    onScroll={handleScroll('experiment')}
                    style={{ overflowX: 'auto', overflowY: 'auto' }}
                  >
                    {diffLines.map((diffLine, index) => (
                      <div key={`backend-${index}`} className={`font-mono text-xs leading-relaxed px-3 py-1 whitespace-nowrap ${
                        diffLine.type === 'delete' ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200' : 
                        diffLine.type === 'insert' ? 'opacity-30' : 
                        'text-gray-700 dark:text-gray-300'
                      }`}>
                        {diffLine.type !== 'insert' && (
                          <>
                            <span className="text-gray-400 mr-2 select-none">
                              {diffLine.backendLineNum?.toString().padStart(4, ' ')}
                            </span>
                            <span className="select-none">
                              {diffLine.type === 'delete' ? '- ' : '  '}
                            </span>
                            <span>{diffLine.backendLine || ''}</span>
                          </>
                        )}
                        {diffLine.type === 'insert' && <span className="invisible">placeholder</span>}
                      </div>
                    ))}
                  </div>
                  
                  {/* Test diff view */}
                  <div 
                    className="bg-gray-50 dark:bg-gray-700 rounded-lg max-h-96 overflow-auto border"
                    ref={userLogRef}
                    onScroll={handleScroll('test')}
                    style={{ overflowX: 'auto', overflowY: 'auto' }}
                  >
                    {diffLines.map((diffLine, index) => (
                      <div key={`user-${index}`} className={`font-mono text-xs leading-relaxed px-3 py-1 whitespace-nowrap ${
                        diffLine.type === 'insert' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200' : 
                        diffLine.type === 'delete' ? 'opacity-30' : 
                        'text-gray-700 dark:text-gray-300'
                      }`}>
                        {diffLine.type !== 'delete' && (
                          <>
                            <span className="text-gray-400 mr-2 select-none">
                              {diffLine.userLineNum?.toString().padStart(4, ' ')}
                            </span>
                            <span className="select-none">
                              {diffLine.type === 'insert' ? '+ ' : '  '}
                            </span>
                            <span>{diffLine.userLine || ''}</span>
                          </>
                        )}
                        {diffLine.type === 'delete' && <span className="invisible">placeholder</span>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-3">实验日志</h4>
                  <div 
                    className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 max-h-96 overflow-auto"
                    ref={backendLogRef}
                    onScroll={handleScroll('experiment')}
                    style={{ overflowX: 'auto', overflowY: 'auto' }}
                  >
                    <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre overflow-x-auto">
                      {backendLog ? formatLogData(backendLog) : '未找到实验日志'}
                    </pre>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-3">测试日志</h4>
                  <div 
                    className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 max-h-96 overflow-auto"
                    ref={userLogRef}
                    onScroll={handleScroll('test')}
                    style={{ overflowX: 'auto', overflowY: 'auto' }}
                  >
                    <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre overflow-x-auto">
                      {userLog ? formatLogData(userLog) : '请上传测试 LOG 文件'}
                    </pre>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Audio Comparison Groups */}
          <div className="space-y-8">
            {/* Prevtrack Group */}
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-xl p-6 border-2 border-purple-200 dark:border-purple-800">
              <h2 className="text-2xl font-bold text-purple-900 dark:text-purple-100 mb-6">
                Prevtrack 音频对比 (Syncout)
              </h2>
              
              <div className="space-y-6">
                {/* Prevtrack User Audio Upload */}
                <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-purple-200 dark:border-purple-700">
                  <h3 className="text-base font-semibold text-purple-900 dark:text-purple-100 mb-3">
                    上传测试音频 (Syncout)
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        选择 syncout 音频文件
                      </label>
                      <input
                        type="file"
                        accept="audio/*"
                        onChange={handlePrevtrackUserAudioUpload}
                        className="block w-full text-sm text-gray-500 dark:text-gray-400
                          file:mr-4 file:py-2 file:px-4
                          file:rounded-full file:border-0
                          file:text-sm file:font-semibold
                          file:bg-purple-50 file:text-purple-700
                          hover:file:bg-purple-100"
                      />
                    </div>
                    {selectedPrevtrackUserAudio && (
                      <div className="flex items-center text-sm text-green-600 dark:text-green-400">
                        <Check className="h-4 w-4 mr-2" />
                        已上传: {selectedPrevtrackUserAudio.name}
                      </div>
                    )}
                  </div>
                </div>

                {/* Prevtrack Aligned Audio Player */}
                <AlignedWaveformPlayer
                  experimentAudioUrl={prevtrackExperimentAudio?.url || null}
                  testAudioUrl={prevtrackUserAudio?.url || null}
                  experimentTitle="Prevtrack 实验音频 (Syncout)"
                  testTitle="Prevtrack 测试音频 (Syncout)"
                  alignmentSegments={prevtrackAlignmentSegments}
                  matchStartTime={prevtrackDtwResult?.matchStartTime}
                  matchEndTime={prevtrackDtwResult?.matchEndTime}
                  onExperimentBufferLoaded={(buffer) => {
                    prevtrackExperimentBufferRef.current = buffer
                    console.log('Prevtrack Experiment AudioBuffer loaded:', buffer.duration, 's')
                  }}
                  onTestBufferLoaded={(buffer) => {
                    prevtrackUserBufferRef.current = buffer
                    console.log('Prevtrack User AudioBuffer loaded:', buffer.duration, 's')
                  }}
                />

                {/* Prevtrack DTW Analysis Section */}
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                      <Activity className="h-5 w-5 mr-2 text-purple-600" />
                      Prevtrack 相似度分析 (DTW)
                    </h3>
                    <button
                      onClick={handleCalculatePrevtrackDTW}
                      disabled={isPrevtrackCalculatingDTW || !prevtrackExperimentAudio || !prevtrackUserAudio}
                      className="inline-flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 
                        disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg 
                        transition-colors font-medium"
                    >
                      {isPrevtrackCalculatingDTW ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          计算中...
                        </>
                      ) : (
                        '对齐&计算相似'
                      )}
                    </button>
                  </div>

                  {prevtrackDtwResult && (
                    <div className="space-y-6">
                      {/* Match Location Info */}
                      {prevtrackDtwResult.matchStartTime !== undefined && prevtrackDtwResult.matchEndTime !== undefined && (
                        <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-800/20 rounded-lg p-4 border border-indigo-200 dark:border-indigo-800">
                          <div className="flex items-center mb-2">
                            <Activity className="h-5 w-5 text-indigo-600 dark:text-indigo-400 mr-2" />
                            <span className="font-semibold text-indigo-900 dark:text-indigo-100">匹配位置定位</span>
                          </div>
                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div>
                              <span className="text-indigo-600 dark:text-indigo-400">起始时间: </span>
                              <span className="font-mono font-bold text-indigo-900 dark:text-indigo-100">
                                {prevtrackDtwResult.matchStartTime.toFixed(2)}s
                              </span>
                            </div>
                            <div>
                              <span className="text-indigo-600 dark:text-indigo-400">结束时间: </span>
                              <span className="font-mono font-bold text-indigo-900 dark:text-indigo-100">
                                {prevtrackDtwResult.matchEndTime.toFixed(2)}s
                              </span>
                            </div>
                            <div>
                              <span className="text-indigo-600 dark:text-indigo-400">匹配时长: </span>
                              <span className="font-mono font-bold text-indigo-900 dark:text-indigo-100">
                                {(prevtrackDtwResult.matchEndTime - prevtrackDtwResult.matchStartTime).toFixed(2)}s
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Summary Cards */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
                          <div className="text-sm text-green-600 dark:text-green-400 font-medium mb-1">
                            相似度
                          </div>
                          <div className="text-3xl font-bold text-green-700 dark:text-green-300">
                            {prevtrackDtwResult.similarity.toFixed(2)}%
                          </div>
                        </div>
                        
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                          <div className="text-sm text-blue-600 dark:text-blue-400 font-medium mb-1">
                            DTW距离
                          </div>
                          <div className="text-3xl font-bold text-blue-700 dark:text-blue-300">
                            {prevtrackDtwResult.normalizedDistance.toFixed(4)}
                          </div>
                        </div>
                        
                        <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
                          <div className="text-sm text-purple-600 dark:text-purple-400 font-medium mb-1">
                            对齐点数
                          </div>
                          <div className="text-3xl font-bold text-purple-700 dark:text-purple-300">
                            {prevtrackDtwResult.path.length}
                          </div>
                        </div>
                      </div>

                      {/* Alignment Details */}
                      {prevtrackAlignmentSegments.length > 0 && (
                        <div>
                          <h4 className="font-medium text-gray-900 dark:text-white mb-3">
                            时间对齐详情
                          </h4>
                          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 max-h-64 overflow-y-auto">
                            <table className="w-full text-sm">
                              <thead className="text-xs text-gray-500 dark:text-gray-400 uppercase border-b border-gray-200 dark:border-gray-600">
                                <tr>
                                  <th className="text-left py-2">实验音频时间</th>
                                  <th className="text-left py-2">测试音频时间</th>
                                  <th className="text-left py-2">时间偏移</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                                {prevtrackAlignmentSegments.map((segment, idx) => (
                                  <tr key={idx} className="text-gray-700 dark:text-gray-300">
                                    <td className="py-2 font-mono">{segment.time1.toFixed(2)}s</td>
                                    <td className="py-2 font-mono">{segment.time2.toFixed(2)}s</td>
                                    <td className={`py-2 font-mono ${
                                      Math.abs(segment.offset) < 0.1 
                                        ? 'text-green-600 dark:text-green-400' 
                                        : 'text-orange-600 dark:text-orange-400'
                                    }`}>
                                      {segment.offset >= 0 ? '+' : ''}{segment.offset.toFixed(2)}s
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {!prevtrackDtwResult && !isPrevtrackCalculatingDTW && (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      点击"对齐&计算相似"按钮开始分析 Prevtrack 音频的相似度和时间对齐
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Nexttrack Group */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-xl p-6 border-2 border-blue-200 dark:border-blue-800">
              <h2 className="text-2xl font-bold text-blue-900 dark:text-blue-100 mb-6">
                Nexttrack 音频对比 (Syncin)
              </h2>
              
              <div className="space-y-6">
                {/* Nexttrack User Audio Upload */}
                <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-blue-200 dark:border-blue-700">
                  <h3 className="text-base font-semibold text-blue-900 dark:text-blue-100 mb-3">
                    上传测试音频 (Syncin)
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        选择 syncin 音频文件
                      </label>
                      <input
                        type="file"
                        accept="audio/*"
                        onChange={handleNexttrackUserAudioUpload}
                        className="block w-full text-sm text-gray-500 dark:text-gray-400
                          file:mr-4 file:py-2 file:px-4
                          file:rounded-full file:border-0
                          file:text-sm file:font-semibold
                          file:bg-blue-50 file:text-blue-700
                          hover:file:bg-blue-100"
                      />
                    </div>
                    {selectedNexttrackUserAudio && (
                      <div className="flex items-center text-sm text-green-600 dark:text-green-400">
                        <Check className="h-4 w-4 mr-2" />
                        已上传: {selectedNexttrackUserAudio.name}
                      </div>
                    )}
                  </div>
                </div>

                {/* Nexttrack Aligned Audio Player */}
                <AlignedWaveformPlayer
                  experimentAudioUrl={nexttrackExperimentAudio?.url || null}
                  testAudioUrl={nexttrackUserAudio?.url || null}
                  experimentTitle="Nexttrack 实验音频 (Syncin)"
                  testTitle="Nexttrack 测试音频 (Syncin)"
                  alignmentSegments={nexttrackAlignmentSegments}
                  matchStartTime={nexttrackDtwResult?.matchStartTime}
                  matchEndTime={nexttrackDtwResult?.matchEndTime}
                  onExperimentBufferLoaded={(buffer) => {
                    nexttrackExperimentBufferRef.current = buffer
                    console.log('Nexttrack Experiment AudioBuffer loaded:', buffer.duration, 's')
                  }}
                  onTestBufferLoaded={(buffer) => {
                    nexttrackUserBufferRef.current = buffer
                    console.log('Nexttrack User AudioBuffer loaded:', buffer.duration, 's')
                  }}
                />

                {/* Nexttrack DTW Analysis Section */}
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                      <Activity className="h-5 w-5 mr-2 text-blue-600" />
                      Nexttrack 相似度分析 (DTW)
                    </h3>
                    <button
                      onClick={handleCalculateNexttrackDTW}
                      disabled={isNexttrackCalculatingDTW || !nexttrackExperimentAudio || !nexttrackUserAudio}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 
                        disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg 
                        transition-colors font-medium"
                    >
                      {isNexttrackCalculatingDTW ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          计算中...
                        </>
                      ) : (
                        '对齐&计算相似'
                      )}
                    </button>
                  </div>

                  {nexttrackDtwResult && (
                    <div className="space-y-6">
                      {/* Match Location Info */}
                      {nexttrackDtwResult.matchStartTime !== undefined && nexttrackDtwResult.matchEndTime !== undefined && (
                        <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-800/20 rounded-lg p-4 border border-indigo-200 dark:border-indigo-800">
                          <div className="flex items-center mb-2">
                            <Activity className="h-5 w-5 text-indigo-600 dark:text-indigo-400 mr-2" />
                            <span className="font-semibold text-indigo-900 dark:text-indigo-100">匹配位置定位</span>
                          </div>
                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div>
                              <span className="text-indigo-600 dark:text-indigo-400">起始时间: </span>
                              <span className="font-mono font-bold text-indigo-900 dark:text-indigo-100">
                                {nexttrackDtwResult.matchStartTime.toFixed(2)}s
                              </span>
                            </div>
                            <div>
                              <span className="text-indigo-600 dark:text-indigo-400">结束时间: </span>
                              <span className="font-mono font-bold text-indigo-900 dark:text-indigo-100">
                                {nexttrackDtwResult.matchEndTime.toFixed(2)}s
                              </span>
                            </div>
                            <div>
                              <span className="text-indigo-600 dark:text-indigo-400">匹配时长: </span>
                              <span className="font-mono font-bold text-indigo-900 dark:text-indigo-100">
                                {(nexttrackDtwResult.matchEndTime - nexttrackDtwResult.matchStartTime).toFixed(2)}s
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Summary Cards */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
                          <div className="text-sm text-green-600 dark:text-green-400 font-medium mb-1">
                            相似度
                          </div>
                          <div className="text-3xl font-bold text-green-700 dark:text-green-300">
                            {nexttrackDtwResult.similarity.toFixed(2)}%
                          </div>
                        </div>
                        
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                          <div className="text-sm text-blue-600 dark:text-blue-400 font-medium mb-1">
                            DTW距离
                          </div>
                          <div className="text-3xl font-bold text-blue-700 dark:text-blue-300">
                            {nexttrackDtwResult.normalizedDistance.toFixed(4)}
                          </div>
                        </div>
                        
                        <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
                          <div className="text-sm text-purple-600 dark:text-purple-400 font-medium mb-1">
                            对齐点数
                          </div>
                          <div className="text-3xl font-bold text-purple-700 dark:text-purple-300">
                            {nexttrackDtwResult.path.length}
                          </div>
                        </div>
                      </div>

                      {/* Alignment Details */}
                      {nexttrackAlignmentSegments.length > 0 && (
                        <div>
                          <h4 className="font-medium text-gray-900 dark:text-white mb-3">
                            时间对齐详情
                          </h4>
                          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 max-h-64 overflow-y-auto">
                            <table className="w-full text-sm">
                              <thead className="text-xs text-gray-500 dark:text-gray-400 uppercase border-b border-gray-200 dark:border-gray-600">
                                <tr>
                                  <th className="text-left py-2">实验音频时间</th>
                                  <th className="text-left py-2">测试音频时间</th>
                                  <th className="text-left py-2">时间偏移</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                                {nexttrackAlignmentSegments.map((segment, idx) => (
                                  <tr key={idx} className="text-gray-700 dark:text-gray-300">
                                    <td className="py-2 font-mono">{segment.time1.toFixed(2)}s</td>
                                    <td className="py-2 font-mono">{segment.time2.toFixed(2)}s</td>
                                    <td className={`py-2 font-mono ${
                                      Math.abs(segment.offset) < 0.1 
                                        ? 'text-green-600 dark:text-green-400' 
                                        : 'text-orange-600 dark:text-orange-400'
                                    }`}>
                                      {segment.offset >= 0 ? '+' : ''}{segment.offset.toFixed(2)}s
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {!nexttrackDtwResult && !isNexttrackCalculatingDTW && (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      点击"对齐&计算相似"按钮开始分析 Nexttrack 音频的相似度和时间对齐
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
